import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, Validators } from "@angular/forms";
import { ApiService } from "../../services/api.service";
import { FormService } from '../../services/form.service';
import { animate, style, transition, trigger } from "@angular/animations";
import Bugsnag from "@bugsnag/js";
import { buildQuestionPayload, questionControlKeys } from './third-party-questions.util';
import { Question } from './question/question.model';

@Component( {
	selector: 'app-post-submit-third-party',
	templateUrl: './third-party-questions.component.html',
	styleUrls: ['./third-party-questions.component.scss'],
	animations: [
		trigger('slideInOut', [
			transition(':enter', [
				style({opacity: 0}),
				animate('300ms ease-in-out', style({opacity: 1}))
			]),
			transition(':leave', [
				style({opacity: 1}),
				animate('300ms ease-in-out', style({opacity: 0}))
			]),
		]),
		// Enter-only, so the previous question doesn't linger stacked above the next one
		trigger('questionFadeIn', [
			transition(':enter', [
				style({opacity: 0}),
				animate('300ms ease-in-out', style({opacity: 1}))
			]),
		])
	]
} )
export class PostSubmitThirdPartyQuestionsComponent implements OnInit {

	public thirdPartyForm !: UntypedFormGroup
	private advancing : boolean = false;

	constructor( public formService: FormService,
	             private apiService: ApiService ) {

	}

	ngOnInit(): void {

		this.thirdPartyForm = this.formService.thirdPartyForm as UntypedFormGroup;
	}

	trackByKey( index: number, question: Question ): number {

		return question.key;
	}

	onSubmit() {

		// Ignore a double-click on Save right after advancing, which would
		// otherwise submit (and silently decline) the next question
		if( this.advancing ) {
			return;
		}

		const questionKey = this.formService.currentQuestion!.key;
		const stepValues = { question_key: questionKey, question_index: this.formService.currentQuestionIndex };

		// The questions are answered one per step (DEV-6874): only the current
		// question's controls are validated and posted
		const controlKeys = questionControlKeys( this.thirdPartyForm, questionKey );

		// Telemetry: post-submit questions "Save" click intent (DEV-6453),
		// before validation so blocked attempts are visible too. No formGroup
		// is passed — stepValues carries this step's answers.
		this.formService.logFormAction( 'questions_submit_clicked', 'thirdPartyQuestions', undefined, stepValues );

		if( controlKeys.some( key => this.thirdPartyForm.controls[key].invalid ) ) {

			var firstErrorFound = false;

			// For unknown reasons we have to mark every single field as dirty manually.
			// This is probably because we create the fields dynamically and something isn't build as intended there
			controlKeys.forEach(key => {

				// If is required
				if( this.thirdPartyForm.controls[key].hasValidator( Validators.required ) ) {

					if( ! firstErrorFound ) {

						// Get form element
						var formEl = document.getElementById( 'third-party-questions-form' );

						// Get form control html element
						var formElementEl = document.getElementById( 'key-' + key );

						// Check if elements exists
						if( formElementEl && formEl ) {

							// Scroll questions form
							formEl.scroll({
								top: formElementEl.offsetTop,
								behavior: 'smooth',
							} );
						}

						// Mark as found to not scroll again
						firstErrorFound = true;
					}

					this.thirdPartyForm.controls[key].markAsDirty();
				}
			} );
			return;
		}

		this.formService.logFormAction( 'questions_submit_success', 'thirdPartyQuestions', undefined, stepValues );

		// Telemetry: the submitted answers themselves (DEV-6558), as entered (DEV-7892).
		this.formService.logThirdPartyAnswersSubmitted( questionKey );

		// Add the data to any potential bugsnag report
		Bugsnag.addMetadata('ThirdPartyQuestions', this.thirdPartyForm.value );

		// Post to super-form before moving on, so this answer is saved even if
		// the consumer drops off at a later question
		this.apiService.postQuestions( buildQuestionPayload( this.thirdPartyForm, questionKey ) ).subscribe();

		if( this.formService.currentQuestionIndex < this.formService.questions.length - 1 ) {

			this.formService.currentQuestionIndex++;
			this.formService.logQuestionShown();

			this.advancing = true;
			setTimeout( () => this.advancing = false, 300 );

			document.getElementById( 'third-party-questions-form' )?.scroll( { top: 0, behavior: 'smooth' } );
			return;
		}

		this.formService.questionsActive = false;
		this.formService.postSubmitStep = 'thankYou';
		this.formService.countDownAndRedirect();
	}
}
